import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Select } from '@creditornot/cb-select';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { APIErrorNotification, RFFFieldErrorWrapper, WoltModal } from 'common/components';
import { localize } from 'common/utils';
import validateYupSchema from 'common/utils/validateYupSchema';
import { languages } from 'config';
import { useCreateSharedMenuTracking } from 'modules/menu/hooks/telemetryHooks';

import { createSharedMenu } from '../api';
import { SHARED_MENU_NAME_MAX_LENGTH } from '../constants';
import { useScopeContext } from '../contexts/ScopeContext';
import { useSharedMenuData } from '../hooks/useSharedMenuData';
import { CreateSharedMenuPayload } from '../types';

type Props = {
  onClose: () => void;
  isOpen: boolean;
};

const Root = styled(WoltModal)`
  width: 520px;
  border-radius: 8px;
  background-color: ${cssThemedValue('surfaceMain')};
  display: flex;
  flex-direction: column;
  overflow: visible;
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
`;

const ModalButtons = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

const languageLabels: { [key in Language]: string } = {
  ar: 'languages.ar',
  'ar-IL': 'languages.ar-IL',
  az: 'languages.az',
  cs: 'languages.cs',
  da: 'languages.da',
  de: 'languages.de',
  ee: 'languages.ee',
  el: 'languages.el',
  en: 'languages.en',
  et: 'languages.et',
  fi: 'languages.fi',
  fr: 'languages.fr',
  he: 'languages.he',
  hr: 'languages.hr',
  hu: 'languages.hu',
  is: 'languages.is',
  ja: 'languages.ja',
  ka: 'languages.ka',
  lt: 'languages.lt',
  lv: 'languages.lv',
  nb: 'languages.nb',
  pl: 'languages.pl',
  ru: 'languages.ru',
  sk: 'languages.sk',
  sl: 'languages.sl',
  sq: 'languages.sq',
  sr: 'languages.sr',
  sv: 'languages.sv',
  uz: 'languages.uz',
};

type FormValues = {
  name: string;
  language: Language | undefined;
};

export const validationSchema = yup.object({
  name: yup
    .string()
    .required()
    .lowercase()
    .trim()
    .max(SHARED_MENU_NAME_MAX_LENGTH)
    .test(
      'name',
      { id: 'shared-menu.validation-messages.menu.empty-name' },
      (value: string | undefined) => typeof value === 'string' && value !== '',
    )
    .when('$allSharedMenusNames', (allSharedMenusNames: string[], fieldSchema: yup.StringSchema) =>
      fieldSchema.test('name', { id: 'shared-menu.validation-messages.menu.duplicated-name' }, value =>
        typeof value === 'string' ? !allSharedMenusNames.includes(value) : true,
      ),
    ),
  language: yup.mixed<Language>().required(),
});

export const CreateSharedMenuModal = ({ isOpen, onClose }: Props) => {
  const { formatMessage } = useIntl();
  const scope = useScopeContext();

  const { refetchSharedMenus, sharedMenus } = useSharedMenuData({ scope });

  const languageOptions = useMemo(() => {
    return languages.map(lang => ({ value: lang, label: formatMessage({ id: languageLabels[lang] }) }));
  }, [formatMessage]);

  const allSharedMenusNames = sharedMenus.map(sm => localize(sm.name, sm.primary_language).toLocaleLowerCase());

  const trackMenuCreation = useCreateSharedMenuTracking();

  const { mutate, isLoading, error, reset } = useMutation(
    (payload: CreateSharedMenuPayload) => createSharedMenu(payload),
    {
      onSuccess: async data => {
        trackMenuCreation(data.id, data.primary_language);
        handleClose();
        await refetchSharedMenus();
        toast.success(
          <FormattedMessage
            id="listing-manager.create-menu.success"
            values={{
              bold: (content: any) => <strong>{content}</strong>,
              menuName: localize(data.name, data.primary_language),
            }}
          />,
        );
      },
    },
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return (
    <Root isOpen={isOpen} onRequestClose={handleClose}>
      <ModalHeader onCloseClick={handleClose}>
        <FormattedMessage id="listing-manager.create-menu.title" />
      </ModalHeader>
      <Form<FormValues>
        initialValues={{
          name: '',
          // TODO: fill with some default language
          language: undefined,
        }}
        onSubmit={values => {
          if (!values.language) {
            return;
          }

          const payload: CreateSharedMenuPayload = {
            name: [
              {
                value: values.name,
                lang: values.language,
                verified: true,
              },
            ],
            primary_language: values.language,
            scope,
          };
          mutate(payload);
        }}
        validate={validateYupSchema(validationSchema, { allSharedMenusNames })}
      >
        {({ handleSubmit, pristine, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            <StyledModalContent>
              <FormRow label={formatMessage({ id: 'shared-menu.new-shared-menu.name' })} horizontalPadding>
                <Field<FormValues['name']> name="name">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <Input
                          {...input}
                          placeholder={formatMessage({ id: 'shared-menu.new-shared-menu.name-placeholder' })}
                        />
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
              <FormRow label={formatMessage({ id: 'shared-menu.new-shared-menu.language' })} horizontalPadding>
                <Field<FormValues['language']> name="language">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => <Select {...input} options={languageOptions} />}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
            </StyledModalContent>
            <StyledModalFooter>
              {error && <APIErrorNotification error={error} style={{ marginBlockEnd: '16px', width: '100%' }} />}
              <ModalButtons>
                <Button variant="lightBlue" size="small" style={{ marginInlineEnd: '8px' }} onClick={handleClose}>
                  <FormattedMessage id="state.cancel" />
                </Button>
                <Button
                  variant="blue"
                  size="small"
                  type="submit"
                  disabled={pristine || hasValidationErrors || isLoading}
                  loading={isLoading}
                >
                  <FormattedMessage id="state.create" />
                </Button>
              </ModalButtons>
            </StyledModalFooter>
          </form>
        )}
      </Form>
    </Root>
  );
};
